// Remembers the answers the endpoint has already given, keyed by the fragment asked about.
//
// A fragment gets the same answer every time until the vocabulary is rebuilt, so the second
// pass over a phrase — backspacing, retyping, or typing the same query again — can be answered
// from here instead of over a round trip, which costs around 300ms from outside the US.
//
// What comes back out is exactly what the endpoint put in, in its order: nothing is filtered,
// narrowed, re-sorted or merged. A dropdown served from here is indistinguishable from a fresh
// one, which is the only reason it is safe to serve at all.
//
// Entries expire, because "until the vocabulary is rebuilt" is a promise this cannot keep. A
// rebuild, a permission change, or a newly captioned asset all change the right answer while a
// tab stays open, and during development a rebuild is exactly what someone is doing between
// two attempts at the same query. A minute keeps the benefit across a burst of typing while
// bounding how long a stale answer can survive.

export const DEFAULT_TTL_MS = 60_000;

// Roughly a session's worth of distinct fragments. The map is insertion-ordered, so the
// oldest entry is the one evicted.
export const DEFAULT_LIMIT = 50;

interface Entry<T> {
  suggestions: T[];
  storedAt: number;
}

export class SuggestionCache<T> {
  private entries = new Map<string, Entry<T>>();

  constructor(
    private ttlMs: number = DEFAULT_TTL_MS,
    private limit: number = DEFAULT_LIMIT,
    private clock: () => number = Date.now
  ) {}

  get(fragment: string): T[] | null {
    const entry = this.entries.get(fragment);
    if (!entry) { return null; }

    if (this.clock() - entry.storedAt >= this.ttlMs) {
      this.entries.delete(fragment);
      return null;
    }

    return entry.suggestions;
  }

  // Empty answers are deliberately not stored. An empty answer is what tells the endpoint to
  // build vocabulary for a resource that has none, so the same fragment has to be able to ask
  // again and pick up whatever that build produced.
  set(fragment: string, suggestions: T[]): void {
    if (!suggestions.length) { return; }

    this.entries.set(fragment, { suggestions, storedAt: this.clock() });

    if (this.entries.size > this.limit) {
      this.entries.delete(this.entries.keys().next().value);
    }
  }
}
